"use client";

import { motion } from "framer-motion";
import type { ReactNode } from "react";

const EASE = [0.76, 0, 0.24, 1] as const;

/**
 * Route transition — re-mounts on every navigation (Next `template`). A premium
 * ink panel carrying the TAHAWER wordmark + a drawing accent line holds briefly,
 * then lifts to reveal the page. Applies site-wide, About Us included.
 */
export default function Template({ children }: { children: ReactNode }) {
  return (
    <>
      <motion.div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[80] flex flex-col items-center justify-center bg-[#0b0b0b]"
        initial={{ y: 0 }}
        animate={{ y: "-100%" }}
        transition={{ duration: 0.7, ease: EASE, delay: 0.45 }}
      >
        <motion.span
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="font-bebas text-4xl uppercase tracking-[0.4em] text-white/95 md:text-5xl"
        >
          TAHAWER
        </motion.span>
        <motion.span
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.12, ease: "easeInOut" }}
          className="mt-5 h-[2px] w-20 origin-center bg-accent"
        />
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-4 text-[10px] uppercase tracking-[0.35em] text-white/35"
        >
          Properties
        </motion.span>
      </motion.div>

      {/* thin accent sweep that trails the panel */}
      <motion.div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 z-[79] h-[3px] origin-left bg-accent"
        initial={{ scaleX: 1 }}
        animate={{ scaleX: 0 }}
        transition={{ duration: 0.6, ease: EASE, delay: 0.55 }}
      />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </>
  );
}
